<template>
  <div class="shadow-lg w-full md:w-1/2 lg:w-1/4">
    <!-- Header -->
    <div
      @click="showBody = !showBody"
      class="bg-primary hover:bg-primary-dark cursor-pointer text-white p-3 flex items-center"
    >
      <div class="flex items-center text-white py-1 px-2 mr-2">
        <base-icon v-if="pendingTasksAmount > 0" icon="spinner-btn" class="icon-spin mr-2" />
        {{ completedTasksAmount }} / {{ tasks.length }}
      </div>
      <span v-if="pendingTasksAmount > 0">{{ tasksLeftText }}</span>
      <base-icon
        icon="chevron-down"
        class="p-1 bg-primary-dark hover:bg-white hover:text-primary ml-auto mr-3 cursor-pointer"
        :class="{'rotate-180': !showBody}"
        @click.native.self="showBody = !showBody"
      />
      <base-icon
        icon="close"
        class="p-1 bg-primary-dark hover:bg-white hover:text-primary cursor-pointer"
        @click.native="$emit('close')"
      />
    </div>

    <!-- Body -->
    <div v-if="showBody" class="body bg-white">
      <div v-if="this.numberOfTasks === 0" class="flex justify-center py-3 px-3">
        <span class="opacity-50">{{ tasksEmptyList }}</span>
      </div>

      <div v-else class="flex justify-end py-3 px-3">
        <span class="cursor-pointer hover:text-primary-light" @click="onClearCompletedTasks">{{
          clearCompletedText
        }}</span>
      </div>

      <!-- Task list -->
      <div class="tasks-list">
        <div v-for="task in tasksWithMetadata" :key="task.id" class="task">
          <div class="flex items-center border-b-2 border-gray-200 py-2 p-3">
            <span class="mr-3"><base-icon :icon="task.icon" :class="task.classes" /></span>
            <span>{{ task.title }}</span>
            <span class="ml-auto flex items-center">
              <span
                v-if="task.report?.text"
                class="hover:text-primary-light cursor-pointer mr-2"
                @click="modal.content = task.report.text; modal.show = true;"
              ><base-icon icon="eyeOutline"
              /></span>
              <span
                v-if="task.report?.file"
                class="hover:text-primary-light cursor-pointer mr-2"
                @click="onDownloadReport(task.id)"
              ><base-icon icon="downloadOutline"
              /></span>
              <span
                class="hover:text-primary-light opacity-0"
                :class="{'opacity-100 cursor-pointer': task.status !== 'pending'}"
                @click="onRemoveTask(task.id)"
              ><base-icon icon="delete"
              /></span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Report -->
    <base-modal v-if="modal.show"
                head="task_report"
                @close="modal.show = false">
      <pre>{{ modal.content }}</pre>
    </base-modal>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {BaseIcon, BaseModal} from '@/components/base'
import fileExport from '@/state/helpers/fileExport'

export default {
  components: {
    BaseIcon,
    BaseModal
  },

  data() {
    return {
      showBody: false,
      modal: {
        show: false,
        content: null
      }
    }
  },

  computed: {
    ...mapGetters('asyncTasksTracker', {
      tasks: 'tasks',
      numberOfTasks: 'tasksAmount',
    }),

    tasksWithMetadata() {
      return this.tasks.map(t => {
        const task = {...t}
        switch (task.status) {
          case 'pending':
            task.icon = 'spinner-btn'
            task.classes = 'icon-spin'
            break
          case 'warning':
            task.icon = 'warn'
            task.classes = 'text-orange-default'
            break
          case 'complete':
            task.icon = 'checkmark'
            task.classes = 'text-green-dark'
            break
          case 'failed':
            task.icon = 'close'
            task.classes = 'text-red-default'
        }
        return {...task}
      })
    },

    completedTasksAmount() {
      return this.tasks.filter(t => t.status !== 'pending').length
    },

    pendingTasksAmount() {
      return this.tasks.length - this.completedTasksAmount
    },

    clearCompletedText() {
      return this.$t('mygwf.components.AsyncTaskTracker.clear_completed')
    },

    tasksLeftText() {
      return this.$i18n.locale === 'el'
        ? `${this.$t('mygwf.components.AsyncTaskTracker.left')} ${this.pendingTasksAmount}`
        : `${this.pendingTasksAmount} ${this.$t('mygwf.components.AsyncTaskTracker.left')}`
    },

    tasksEmptyList() {
      return `${this.$t('mygwf.components.AsyncTaskTracker.empty_list')}`
    },
  },

  methods: {

    onDownloadReport(id) {
      if (!id) {
        return
      }
      const task = this.$store.getters['asyncTasksTracker/getTaskById'](id);
      if (!task?.report?.file) {
        return
      }
      fileExport(task.report.file, task.report.fileName);
    },

    onRemoveTask(id) {
      if (!id) {
        return
      }
      const task = this.$store.getters['asyncTasksTracker/getTaskById'](id);
      if (task.status === 'pending') {
        return
      }
      this.$store.dispatch('asyncTasksTracker/removeTask', id)
    },

    onClearCompletedTasks() {
      this.$store.dispatch('asyncTasksTracker/clearCompletedTasks')
      this.showBody = this.tasks.length > 0
    },
  },
}
</script>

<style scoped>
.tasks-list {
  max-height: 350px;
  overflow-y: auto;
}
</style>
